import type { ColumnPropertySpec } from '../../../stores/utils/column-property-spec';

/**
 * The hint shown under a cell editor, derived from the column's property spec.
 *
 * Pure and standalone so it can be unit-tested without the panel's Lit /
 * design-system imports, matching `should-toggle.ts` and
 * `should-open-on-click.ts` beside it.
 *
 * Two things are worth saying about a cell, and only where they are not
 * already obvious from looking at it:
 *
 * - **The item format**, where the compiler enforces one. This is the half
 *   that actually errors: `whatsNext` splits on `|` and then demands
 *   `value~destination`, and it is the second rule that fails a publish.
 * - **The accepted separators**, which are genuinely undiscoverable. Most list
 *   columns split on `/[,;|]/`, so someone assuming commas never finds `|` —
 *   the separator PSTUDIO-3882 was about. But NOT all: `nodeInput` is comma
 *   only and rejects `~ | =` outright, which is why the set comes from the
 *   spec per column rather than being hardcoded here.
 *
 * Returns `undefined` when there is nothing worth saying. Do NOT pad this out
 * with hints restating the label ("Enter a node name"); a hint under every
 * field is noise, and noise is how the useful one gets ignored.
 */
export const getFieldHint = (spec: ColumnPropertySpec): string | undefined => {
  const separators = spec.separators?.length
    ? spec.separators.join(' ')
    : undefined;

  if (spec.itemFormat) {
    return separators
      ? `Format ${spec.itemFormat}, separated by ${separators}`
      : `Format ${spec.itemFormat}`;
  }

  if (!separators) return undefined;

  // A PICKER never sees a typed separator — the user checks options and the
  // committing converter joins them. `behaviors` records the separators its
  // converter accepts (it has to: a pasted or imported value still goes
  // through them), but showing "Separate values with , ; |" under a chip
  // multi-select tells the reader to do something the control does not allow.
  if (spec.kind === 'multiselect' || spec.kind === 'select') return undefined;

  // No item shape, so the only thing to teach is the delimiter. `nodeRefs`
  // says what the values ARE, because its editor is otherwise identical to the
  // free-text ones and "values" would not tell anyone they must be numbers.
  return spec.kind === 'nodeRefs'
    ? `Node numbers, separated by ${separators}`
    : `Separate values with ${separators}`;
};

/**
 * The single line shown under a field in the node property panel.
 *
 * Prefers the spec's CURATED `help` over the generated hint. The two answer
 * different questions — `help` says what the field is for, `getFieldHint` says
 * what shape its value must take — but where a curated line already names the
 * format ("written as outcome~nodeNumber and separated by pipes") showing both
 * would just say it twice. So this is a preference, not a concatenation.
 */
export const getFieldHelp = (spec: ColumnPropertySpec): string | undefined =>
  spec.help ?? getFieldHint(spec);
